import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { Constants } from '@constants';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountIsMicroLoan } from '@helpers/Accounts/getAccountIsMicroLoan';
import { getAccountIsRBL } from '@helpers/Accounts/getAccountIsRBL';
import { getAccountStatus } from '@helpers/Accounts/getAccountStatus';
import { getInLoanDrawdownOffer } from '@helpers/Accounts/getInLoanDrawdownOffer';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type TUseGetRepaymentsPanelData = {
    isOverdue: boolean;
    canTopUpAfterRepayments: boolean;
    amountToRepayBeforeTopUp: number | undefined;
    isRBL: boolean;
    isMicroloan: boolean;
};

export const useGetRepaymentsPanelData = (
    accountId: NonNullable<TAccount['account_id']>
): TUseGetRepaymentsPanelData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    if (!account) {
        throw new Error();
    }

    return {
        isOverdue: getAccountStatus(account) === 'overdue',
        canTopUpAfterRepayments: canTopUpAfterRepayments(account) || false,
        amountToRepayBeforeTopUp: amountToRepayBeforeTopUp(account),
        isRBL: getAccountIsRBL(account),
        isMicroloan: getAccountIsMicroLoan(account)
    };
};

const canTopUpNow = (account: TAccount) => {
    return (
        isTopUpEligible(account) &&
        account.topup_eligibility?.amount_to_repay_before_topup === 0
    );
};

const isTopUpEligible = (account: TAccount) => {
    return account.topup_eligibility?.is_allowed;
};

const hasAvailableCash = (account: TAccount) => {
    const drawdownOffer = getInLoanDrawdownOffer(account);
    return drawdownOffer?.available_cash
        ? drawdownOffer?.available_cash >= Constants.MIN_DRAW_DOWN_AMOUNT
        : false;
};
const canTopUpAfterRepayments = (account: TAccount) => {
    return (
        !canTopUpNow(account) &&
        isTopUpEligible(account) &&
        !hasAvailableCash(account)
    );
};

const amountToRepayBeforeTopUp = (account: TAccount) => {
    return account.topup_eligibility?.amount_to_repay_before_topup;
};
